import {
  Button,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UseDisclosureReturn,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Messages } from "../../core/api";
import { dateFormatter } from "../../shared/utils/date-formatter";
import React from "react";
import { ICD10CodeId } from "../schema/schema";
import { useCreateBulkDiagnosisCodesForPatientMutation } from "../hooks/useCreateBulkDiagnosisCodesForPatientMutation";

export function DiagnosisCodesFetchedModal({
  diagnosisCodesDetails,
  ...modal
}: UseDisclosureReturn & {
  diagnosisCodesDetails: Messages["AuthorizationFileDiagnosisCodeFetched"];
}) {
  const toast = useToast({
    position: "top-right",
  });
  const [codesToApply, setCodesToApply] = React.useState<Set<ICD10CodeId>>(new Set());

  const mutation = useCreateBulkDiagnosisCodesForPatientMutation({
    endDate: diagnosisCodesDetails.authorizationStartDate,
    startDate: diagnosisCodesDetails.authorizationEndDate,
    patientContractId: diagnosisCodesDetails.patientContractId,
  });

  const toggleCode = (code: ICD10CodeId) => {
    setCodesToApply((prev) => {
      if (prev.has(code)) {
        prev.delete(code);
      } else {
        prev.add(code);
      }
      return new Set(prev);
    });
  };

  const submit = () => {
    if (codesToApply.size === 0) {
      return;
    }

    mutation.mutate(Array.from(codesToApply), {
      onSuccess: () => {
        toast({
          title: "Diagnosis codes applied",
          status: "success",
          isClosable: true,
        });
        modal.onClose();
      },
      onError: () => {
        toast({
          title: "Error applying diagnosis codes, try again later",
          status: "error",
          isClosable: true,
        });
      },
    });
  };

  return (
    <Modal size="xl" {...modal}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>Diagnosis Codes Fetched From Authorization File</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Flex} flexDirection="column" gap={4}>
          <Text>
            These codes was fetched from a file you uploaded to patient{" "}
            {diagnosisCodesDetails.patientName} for authorization of{" "}
            {dateFormatter.toDate(diagnosisCodesDetails.authorizationStartDate)}-
            {dateFormatter.toDate(diagnosisCodesDetails.authorizationEndDate)}
          </Text>
          <VStack alignItems="start" spacing={4} w="full">
            {diagnosisCodesDetails.diagnosisCodes.map((code) => (
              <HStack key={code.id} spacing={4}>
                <Checkbox
                  id={`dx-${code.id}`}
                  isChecked={codesToApply.has(code.id)}
                  name={`dx-${code.id}`}
                  onChange={() => toggleCode(code.id)}
                />
                <label htmlFor={`dx-${code.id}`}>
                  {code.formattedCode} - {code.description}
                </label>
              </HStack>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter gap={4}>
          <Button
            colorScheme="blue"
            isDisabled={codesToApply.size === 0 || mutation.isPending}
            isLoading={mutation.isPending}
            variant="solid"
            onClick={submit}
          >
            Apply
          </Button>
          <Button variant="outline" onClick={modal.onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
